/**
 * Optional storefront chrome from color_overrides.
 * Unset keys leave the default Header (HFF-safe). Wilderness Wellness
 * sets headerBackground / headerCtaLabel so the mustard header, larger
 * logo, Book now pill, and Shop-first nav match Squarespace without a
 * global Header.css rewrite.
 */

/** m084 / app_registry ecommerce min_plan_tier — Grower own bit. */
export const ECOMMERCE_PLAN_BIT = 4;

/** True when the tenant plan includes the eCommerce module (Shop/Cart/Login chrome). */
export function tenantHasEcommerce(tierLevel) {
  return ((Number(tierLevel) || 0) & ECOMMERCE_PLAN_BIT) !== 0;
}

export function isCssColorValue(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  return (
    v.startsWith('#') ||
    v.startsWith('rgb') ||
    v.startsWith('hsl') ||
    v.startsWith('var(') ||
    v === 'transparent' ||
    v === 'currentColor'
  );
}

export function chromeFromColors(colors = {}) {
  const cta = String(colors.headerCtaLabel || '').trim();
  return {
    headerBackground: isCssColorValue(colors.headerBackground) ? colors.headerBackground : '',
    headerCtaLabel: cta,
    headerCtaUrl: String(colors.headerCtaUrl || '/appointments').trim() || '/appointments',
    hideLogin: colors.hideLogin === true || colors.hideLogin === 'true',
    logoHeight: colors.headerLogoHeight ? String(colors.headerLogoHeight) : '',
    logoMaxWidth: colors.headerLogoMaxWidth ? String(colors.headerLogoMaxWidth) : '',
  };
}

/**
 * Site Builder fidelity P2: resolve header chrome from first-class settings
 * columns, falling back to color_overrides keys (Wilderness Wellness path).
 * Unset values leave Header.css defaults (48px / 200px / left / half-split).
 */
export function resolveHeaderChrome(settings = {}, colors = {}) {
  const fromColors = chromeFromColors(colors);
  const cfg = settings.chrome_config && typeof settings.chrome_config === 'object'
    ? settings.chrome_config
    : {};

  const pxOrEmpty = (columnVal, fallbackStr) => {
    if (columnVal !== null && columnVal !== undefined && columnVal !== '') {
      const n = Number(columnVal);
      if (Number.isFinite(n) && n > 0) return String(Math.round(n));
    }
    return fallbackStr || '';
  };

  let logoPlacement = '';
  if (settings.logo_placement) {
    const p = String(settings.logo_placement).trim().toLowerCase();
    if (p === 'center') logoPlacement = 'center';
  }

  let navPrimaryCount = null;
  if (cfg.nav_primary_count !== undefined && cfg.nav_primary_count !== null && cfg.nav_primary_count !== '') {
    const n = Number(cfg.nav_primary_count);
    if (Number.isFinite(n)) navPrimaryCount = Math.floor(n);
  }

  return {
    ...fromColors,
    logoHeight: pxOrEmpty(settings.logo_height, fromColors.logoHeight),
    logoMaxWidth: pxOrEmpty(settings.logo_max_width, fromColors.logoMaxWidth),
    logoPlacement,
    navPrimaryCount,
  };
}

/** Split nav into primary / secondary. null count = legacy half-split. */
export function splitNavItems(items = [], primaryCount = null) {
  const n = items.length;
  let split;
  if (primaryCount === null || primaryCount === undefined || primaryCount === '') {
    split = Math.ceil(n / 2);
  } else {
    const parsed = Math.floor(Number(primaryCount));
    split = Number.isFinite(parsed) ? Math.max(0, Math.min(n, parsed)) : Math.ceil(n / 2);
  }
  return {
    primary: items.slice(0, split),
    secondary: items.slice(split),
  };
}

const SLUG_ORDER = {
  '': 0,
  home: 0,
  shopping: 1,
  shop: 1,
  services: 2,
  cft: 3,
  about: 4,
  contact: 5,
  cart: 97,
  'account/login': 99,
  login: 99,
};

export function navSlug(item) {
  const path = String(item?.path || '').replace(/^\//, '');
  if (!path || path === 'home') return '';
  return path;
}

/**
 * P5: nest CMS nav items with parent_id under their parent.
 * Missing/unknown parent → treat as top-level (HFF-safe; no items vanish).
 * Returns top-level items only; children live on `.children`.
 */
export function nestNavChildren(items = []) {
  const list = items.map((item) => ({ ...item, children: [] }));
  const byId = new Map();
  for (const item of list) {
    if (item.id != null) byId.set(String(item.id), item);
  }
  const roots = [];
  for (const item of list) {
    const pid = item.parent_id != null && item.parent_id !== '' ? String(item.parent_id) : null;
    if (pid && byId.has(pid) && pid !== String(item.id || '')) {
      byId.get(pid).children.push(item);
    } else {
      roots.push(item);
    }
  }
  // Drop empty children arrays so flat HFF items stay shape-identical for consumers that ignore them.
  for (const item of list) {
    if (!item.children.length) delete item.children;
  }
  return roots;
}

export function buildStorefrontNav({
  navigation = [],
  replacements = {},
  chrome = {},
  cartCount = 0,
  hasEcommerce = false,
} = {}) {
  const shoppingReplaced = Boolean(replacements.shopping && replacements.shopping.path);
  // Site Builder fidelity P1: Shop/Cart/Login only when plan has ecommerce bit.
  const functionalPages = [
    {
      path: '/shopping',
      label: 'Shop',
      order: 10,
      hideInNav: !hasEcommerce || shoppingReplaced,
    },
    {
      path: '/cart',
      label: cartCount > 0 ? `Cart (${cartCount})` : 'Cart',
      order: 98,
      hideInNav: !hasEcommerce || !chrome.headerCtaLabel,
    },
    {
      path: '/account/login',
      label: 'Login',
      order: 99,
      hideInNav: !hasEcommerce || chrome.hideLogin,
    },
  ];

  const cmsPages = navigation.map((nav) => ({
    id: nav.id,
    path: nav.path,
    label: nav.label,
    type: nav.type,
    parent_id: nav.parent_id || null,
    order: nav.type === 'home' ? 0 :
           nav.type === 'about' ? 2 :
           nav.type === 'contact' ? 20 :
           nav.type === 'faq' ? 21 : 5,
  }));

  // Nest CMS children before merging functional pages (Shop/Cart/Login stay top-level).
  const nestedCms = nestNavChildren(cmsPages);
  const allPages = [...nestedCms, ...functionalPages.filter((page) => !page.hideInNav)];
  return sortNavItems(allPages, { matchSquarespace: Boolean(chrome.headerCtaLabel) });
}

export function sortNavItems(items, { matchSquarespace } = {}) {
  const list = [...items];
  if (!matchSquarespace) {
    return list.sort((a, b) => (a.order ?? 50) - (b.order ?? 50));
  }
  return list.sort((a, b) => {
    const ao = SLUG_ORDER[navSlug(a)] ?? 50;
    const bo = SLUG_ORDER[navSlug(b)] ?? 50;
    if (ao !== bo) return ao - bo;
    return String(a.label || '').localeCompare(String(b.label || ''));
  });
}
